import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Download, Copy, CheckCircle2, AlertCircle, Clock, History,
  FilePlus2, FileEdit, FileX2, ShieldCheck, Info, RefreshCw, Lock, Mail, XCircle,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui'

interface Operation {
  type: string
  id: string
  [key: string]: unknown
}

interface DiffResponse {
  operations: Operation[]
  hasPreviousDeploy: boolean
  lastDeployedAt?: string
}

interface Deployment {
  id: string
  structure_id: string
  status: 'pending' | 'deployed' | 'result_received' | 'failed'
  result_summary: string | null
  notes: string | null
  deployed_at: string
  deployed_by_user_id: string | null
  result_received_at: string | null
  expires_at: string | null
}

interface Approval {
  id: string
  structureId: string
  role: 'primary' | 'deputy'
  status: 'pending' | 'approved' | 'rejected' | 'superseded'
  approverName: string
  approverEmail: string
  tanssTicketId: string | null
  createdAt: string
  respondedAt: string | null
  responseNote: string | null
  lastReminderAt: string | null
  reminderCount: number
  sendError: string | null
  version: number
  structureStatus: 'not_required' | 'pending' | 'approved' | 'rejected'
  deploymentBlocked: boolean
}

interface Props {
  customerId: string
  dirty: boolean
}

const OP_ICONS: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  rename_folder: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'Ordner umbenennen' },
  rename_group: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'Gruppe umbenennen' },
  rename_ou: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'OU umbenennen' },
  rename_server: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'Server umbenennen' },
  rename_share: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'Freigabe umbenennen' },
  create_folder: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Ordner erstellen' },
  create_group: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Gruppe erstellen' },
  create_ou: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'OU erstellen' },
  create_server: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Server erstellen' },
  create_share: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Freigabe erstellen' },
  create_drive: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Laufwerk erstellen' },
  update_drive: { icon: FileEdit, color: 'text-amber-600 bg-amber-50', label: 'Laufwerk ändern' },
  create_user: { icon: FilePlus2, color: 'text-green-600 bg-green-50', label: 'Benutzer erstellen' },
  update_permissions: { icon: ShieldCheck, color: 'text-blue-600 bg-blue-50', label: 'Berechtigung anpassen' },
  warn_delete_folder: { icon: FileX2, color: 'text-red-600 bg-red-50', label: 'Ordner (Warnung)' },
  warn_delete_group: { icon: FileX2, color: 'text-red-600 bg-red-50', label: 'Gruppe (Warnung)' },
  warn_delete_ou: { icon: FileX2, color: 'text-red-600 bg-red-50', label: 'OU (Warnung)' },
  warn_delete_server: { icon: FileX2, color: 'text-red-600 bg-red-50', label: 'Server (Warnung)' },
  warn_delete_drive: { icon: FileX2, color: 'text-red-600 bg-red-50', label: 'Laufwerk (Warnung)' },
}

type DeploymentStatus = Deployment['status']

const STATUS_VARIANT: Record<DeploymentStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  pending: 'neutral',
  deployed: 'info',
  result_received: 'success',
  failed: 'danger',
}

const STATUS_LABEL: Record<DeploymentStatus, string> = {
  pending: 'Ausstehend',
  deployed: 'Exportiert',
  result_received: 'Erfolgreich',
  failed: 'Fehlgeschlagen',
}

function describeOp(op: Operation): string {
  switch (op.type) {
    case 'rename_folder': return `${op['old_path']} → ${op['new_path']}`
    case 'rename_group':
    case 'rename_ou':
    case 'rename_server': return `${op['old_name']} → ${op['new_name']}`
    case 'rename_share': return `${op['old_unc']} → ${op['new_unc']}`
    case 'create_folder': return String(op['path'] ?? '')
    case 'create_group': return `${op['groupType']}_${op['name']}`
    case 'create_ou':
    case 'create_server': return String(op['name'] ?? '')
    case 'create_share': return `${op['name']}${op['isHidden'] ? ' (versteckt)' : ''} (${op['uncPath']})`
    case 'create_drive': {
      const sub = String(op['subPath'] ?? '').replace(/^\\+|\\+$/g, '')
      return `${op['letter']}: → ${op['label']}${sub ? ` (\\${sub})` : ''} [${op['driveType']}]`
    }
    case 'update_drive': return `${op['old_target']}  →  ${op['new_target']}`
    case 'create_user': return String(op['username'] ?? '')
    case 'update_permissions': return `${op['path']}: ${op['old_rights']} → ${op['new_rights']}`
    case 'warn_delete_folder': return String(op['path'] ?? '')
    case 'warn_delete_group':
    case 'warn_delete_ou':
    case 'warn_delete_server': return String(op['name'] ?? '')
    case 'warn_delete_drive': return `${op['letter']}: (${op['label']})`
    default: return JSON.stringify(op)
  }
}

export function DeploymentTab({ customerId, dirty }: Props) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [exportedPayload, setExportedPayload] = useState<string | null>(null)
  const [exportedId, setExportedId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [exportError, setExportError] = useState<string | null>(null)

  const { data: diff, refetch: refetchDiff, isLoading: diffLoading } = useQuery<DiffResponse>({
    queryKey: ['it-customer-diff', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/diff`).then((r) => r.data),
  })

  const { data: histData } = useQuery<{ deployments: Deployment[] }>({
    queryKey: ['it-customer-deployments', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/deployments`).then((r) => r.data),
  })

  const { data: approvalsData } = useQuery<{ approvals: Approval[] }>({
    queryKey: ['it-customer-approvals', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/approvals`).then((r) => r.data),
    refetchInterval: 30_000,
  })

  const deployments = histData?.deployments ?? []
  const approvals = approvalsData?.approvals ?? []
  const latestStructureId = approvals[0]?.structureId
  const latestApprovals = latestStructureId
    ? approvals.filter((a) => a.structureId === latestStructureId)
    : []
  const latestStructureStatus = latestApprovals[0]?.structureStatus ?? null
  const latestDeploymentBlocked = Boolean(latestApprovals[0]?.deploymentBlocked)
  const pendingPrimaryNames = latestApprovals
    .filter((a) => a.role === 'primary' && a.status === 'pending')
    .map((a) => a.approverName)

  const exportMut = useMutation({
    mutationFn: () =>
      apiClient.post(`/it-structures/customers/${customerId}/export`, { notes: notes || undefined }),
    onSuccess: (r) => {
      setExportedPayload(r.data.encrypted_payload)
      setExportedId(r.data.deployment_id)
      setExportError(null)
      setNotes('')
      qc.invalidateQueries({ queryKey: ['it-customer-deployments', customerId] })
      qc.invalidateQueries({ queryKey: ['it-customer-diff', customerId] })
    },
    onError: (e: Error) => setExportError(e.message),
  })

  async function copyPayload() {
    if (!exportedPayload) return
    try {
      await navigator.clipboard.writeText(exportedPayload)
      toast({ title: 'Kopiert', description: 'Payload liegt in der Zwischenablage.', tone: 'success' })
    } catch {
      toast({
        title: 'Kopieren fehlgeschlagen',
        description: 'Textfeld markieren und manuell kopieren.',
        tone: 'warning',
      })
    }
  }

  const ops = diff?.operations ?? []

  return (
    <div className="space-y-4">
      {dirty && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="px-4 py-3 text-body text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Du hast noch ungespeicherte Änderungen. Bitte oben <b>Speichern</b> klicken bevor du exportierst —
              der Export bezieht sich immer auf die zuletzt gespeicherte Version.
            </span>
          </div>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title className="flex items-center gap-2 text-body">
            <FileEdit className="w-4 h-4" />
            Änderungen (Diff gegen letztes Deployment)
          </Card.Title>
          <Button variant="ghost" size="xs" leadingIcon={<RefreshCw className="w-3.5 h-3.5" />} onClick={() => refetchDiff()}>
            Aktualisieren
          </Button>
        </Card.Header>
        {diffLoading ? (
          <LoadingState label="Lade Diff…" />
        ) : !diff?.hasPreviousDeploy ? (
          <div className="px-4 py-3 text-body text-gray-600 bg-blue-50 border-t border-blue-100">
            <Info className="inline w-4 h-4 mr-1 text-blue-500" />
            Noch kein früheres Deployment vorhanden. Beim ersten Export werden alle Elemente als <b>create</b> übertragen.
          </div>
        ) : ops.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<CheckCircle2 />}
            title="Keine Änderungen"
            description="Seit dem letzten Deployment wurden keine Änderungen erfasst."
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {ops.map((op, i) => {
              const cfg = OP_ICONS[op.type] ?? { icon: Info, color: 'text-gray-500 bg-gray-50', label: op.type }
              const Icon = cfg.icon
              return (
                <li key={`${op.type}-${op.id}-${i}`} className="px-4 py-2 flex items-start gap-2">
                  <span className={`p-1 rounded ${cfg.color} flex-shrink-0`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-caption font-medium text-gray-500">{cfg.label}</p>
                    <p className="text-body text-gray-800 font-mono break-all">{describeOp(op)}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Card>
        <Card.Header>
          <Card.Title className="flex items-center gap-2 text-body">
            <Download className="w-4 h-4" />
            Payload exportieren
          </Card.Title>
        </Card.Header>
        <Card.Body className="space-y-3">
          <Input
            label="Notizen (optional)"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="z.B. Ticket #1234, Wartungsfenster 02.05."
          />
          <Button
            variant="primary"
            leadingIcon={<Download className="w-4 h-4" />}
            loading={exportMut.isPending}
            disabled={exportMut.isPending || dirty || latestDeploymentBlocked}
            onClick={() => exportMut.mutate()}
            title={
              latestDeploymentBlocked
                ? pendingPrimaryNames.length > 0
                  ? `Wartet auf Freigabe von: ${pendingPrimaryNames.join(', ')}`
                  : 'Freigabe wurde abgelehnt — bitte neue Version anlegen'
                : undefined
            }
          >
            {exportMut.isPending ? 'Generiere…' : 'Verschlüsselten Payload generieren'}
          </Button>

          {latestDeploymentBlocked && (
            <Card className={latestStructureStatus === 'rejected' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}>
              <Card.Body className="flex items-start gap-2 text-body">
                {latestStructureStatus === 'rejected' ? (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    {latestStructureStatus === 'rejected'
                      ? 'Deployment blockiert — Freigabe wurde abgelehnt'
                      : `Deployment blockiert — wartet auf ${pendingPrimaryNames.length} Freigabe${pendingPrimaryNames.length === 1 ? '' : 'n'}`}
                  </p>
                  {pendingPrimaryNames.length > 0 && (
                    <p className="text-caption text-gray-600 mt-1">
                      Ausstehend: {pendingPrimaryNames.join(', ')}
                    </p>
                  )}
                  {latestStructureStatus === 'rejected' && (
                    <p className="text-caption text-gray-600 mt-1">
                      Bitte neue Version mit Anpassungen anlegen.
                    </p>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}

          {exportError && (
            <ErrorState
              size="sm"
              title="Fehler beim Export"
              description={exportError}
            />
          )}

          {exportedPayload && (
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-caption font-medium text-gray-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Payload erstellt · Deployment-ID:{' '}
                  <code className="font-mono text-caption">{exportedId}</code>
                </p>
                <Button
                  variant="secondary"
                  size="xs"
                  leadingIcon={<Copy className="w-3.5 h-3.5" />}
                  onClick={copyPayload}
                >
                  In Zwischenablage
                </Button>
              </div>
              <textarea
                readOnly
                value={exportedPayload}
                className="w-full h-32 px-3 py-2 border border-gray-200 bg-gray-50 rounded font-mono text-caption text-gray-700 resize-none"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              />
              <Card className="border-blue-200 bg-blue-50">
                <div className="p-3 text-caption text-blue-700 space-y-1">
                  <p className="font-semibold">Nächste Schritte:</p>
                  <ol className="list-decimal list-inside space-y-0.5 pl-1">
                    <li>Payload kopieren und ins PowerShell-Script auf dem Kundenserver einsetzen</li>
                    <li>Script ausführen (als Domain Admin, PS 5.1)</li>
                    <li>Das Script entschlüsselt den Payload, führt die Operationen aus und meldet das Ergebnis zurück</li>
                    <li>Status wird unten in der Historie automatisch aktualisiert</li>
                  </ol>
                </div>
              </Card>
            </div>
          )}
        </Card.Body>
      </Card>

      {approvals.length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center gap-2 text-body">
              <ShieldCheck className="w-4 h-4" />
              Freigaben (neueste zuerst)
            </Card.Title>
          </Card.Header>
          <DataTable
            data={approvals}
            getRowId={(a) => a.id}
            enableSorting={false}
            columns={[
              {
                accessorKey: 'version',
                header: 'Version',
                cell: ({ row }) => `v${row.original.version}`,
              },
              {
                accessorKey: 'role',
                header: 'Rolle',
                cell: ({ row }) => (
                  <Badge size="sm" variant={row.original.role === 'deputy' ? 'info' : 'brand'}>
                    {row.original.role === 'deputy' ? 'Stellv.' : 'Primary'}
                  </Badge>
                ),
              },
              {
                accessorKey: 'approverName',
                header: 'Verantwortlicher',
                cell: ({ row }) => (
                  <div>
                    <div className="text-body">{row.original.approverName}</div>
                    <div className="text-caption text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {row.original.approverEmail}
                    </div>
                  </div>
                ),
              },
              {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => {
                  const variant =
                    row.original.status === 'approved' ? 'success'
                    : row.original.status === 'rejected' ? 'danger'
                    : row.original.status === 'superseded' ? 'neutral'
                    : 'warning'
                  return (
                    <div>
                      <Badge size="sm" variant={variant}>{row.original.status}</Badge>
                      {row.original.reminderCount > 0 && (
                        <span className="text-caption text-gray-500 ml-1">· {row.original.reminderCount}× erinnert</span>
                      )}
                    </div>
                  )
                },
              },
              {
                accessorKey: 'createdAt',
                header: 'Angefragt',
                cell: ({ row }) => (
                  <span className="text-caption text-gray-600">
                    {new Date(row.original.createdAt).toLocaleString('de-DE')}
                  </span>
                ),
              },
              {
                accessorKey: 'respondedAt',
                header: 'Reagiert',
                cell: ({ row }) => (
                  <span className="text-caption text-gray-600">
                    {row.original.respondedAt ? new Date(row.original.respondedAt).toLocaleString('de-DE') : '—'}
                  </span>
                ),
              },
              {
                id: 'note',
                header: 'Notiz / Fehler',
                cell: ({ row }) => (
                  <span className="text-caption text-gray-600">
                    {row.original.sendError
                      ? <span className="text-red-600">Mail-Fehler: {row.original.sendError}</span>
                      : (row.original.responseNote ?? '—')}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title className="flex items-center gap-2 text-body">
            <History className="w-4 h-4" />
            Deployment-Historie
          </Card.Title>
        </Card.Header>
        {deployments.length === 0 ? (
          <EmptyState size="sm" title="Noch keine Deployments" />
        ) : (
          <DataTable
            data={deployments}
            getRowId={(d) => d.id}
            enableSorting={false}
            columns={[
              {
                accessorKey: 'deployed_at',
                header: 'Datum',
                cell: ({ row }) => (
                  <div className="flex items-center gap-1 text-caption text-gray-600">
                    <Clock className="w-3 h-3 text-gray-400" />
                    {new Date(row.original.deployed_at).toLocaleString('de-DE')}
                  </div>
                ),
              },
              {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => (
                  <Badge variant={STATUS_VARIANT[row.original.status] ?? 'neutral'} size="sm">
                    {STATUS_LABEL[row.original.status] ?? row.original.status}
                  </Badge>
                ),
              },
              {
                accessorKey: 'notes',
                header: 'Notizen',
                cell: ({ row }) => (
                  <span className="text-caption text-gray-600">{row.original.notes ?? '—'}</span>
                ),
              },
              {
                id: 'result',
                header: 'Ergebnis',
                cell: ({ row }) => (
                  <span className="text-caption text-gray-600">
                    {row.original.result_summary ?? (row.original.result_received_at ? '✓' : '—')}
                  </span>
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  )
}
